/**
 * jwt — madre de la lectura LOCAL del JWT (DC-5a/b, orden 8f3a2596).
 *
 * El servidor es la única autoridad de validez (firma+revocación); aquí solo se
 * lee el claim `exp` para decisiones de HIGIENE del cliente: no rehidratar una
 * sesión ya muerta, no reintentar un SSE con token expirado (un 401 no se cura
 * reintentando), purgar storage. Nunca se usa para CONCEDER nada.
 */

/** Epoch (segundos) del claim exp, o null si el token no lo trae / no parsea. */
export function expEpochDeJwt(token: string | null | undefined): number | null {
  if (!token) return null
  const partes = token.split('.')
  if (partes.length !== 3) return null
  try {
    const b64 = partes[1].replace(/-/g, '+').replace(/_/g, '/')
    const claims = JSON.parse(atob(b64))
    return typeof claims?.exp === 'number' ? claims.exp : null
  } catch {
    return null
  }
}

/**
 * true si el token está expirado (con margen opcional en segundos). Un token
 * sin exp legible se considera NO expirado aquí — la autoridad sigue siendo el
 * servidor (401), esto solo corta lo obviamente muerto.
 */
export function tokenExpirado(token: string | null | undefined, margenSegundos = 0): boolean {
  const exp = expEpochDeJwt(token)
  if (exp === null) return false
  return Date.now() / 1000 >= exp - margenSegundos
}

/**
 * Vigencia legible de la sesión para la UI ("válida hasta HH:MM") — DC-2 (8f3a2596).
 * S23 aplicado a la interfaz: el secreto JAMÁS se pinta; lo único útil de un token
 * para un humano es hasta cuándo vale. null si no hay exp legible.
 */
export function vigenciaSesion(token: string | null | undefined): string | null {
  const exp = expEpochDeJwt(token)
  if (exp === null) return null
  const d = new Date(exp * 1000)
  const hoy = new Date().toDateString() === d.toDateString()
  const hora = d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  return hoy ? `válida hasta las ${hora}` : `válida hasta ${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}, ${hora}`
}
